import { useMemo } from 'react';
import { getSocketOutwardDir, computeOrthoMidShifts, computeOrthoPaths, bezierControlOffset } from '../utils/edgeMath';
import { computeForceAdjustedControlPoints, type AdjustedControlPoints } from '../utils/forceEdgeLayout';
import { buildEdgeTints } from '../utils/edgeTint';
import type { Edge, Node, NodeGroup } from '../types';

interface ConnectionGeometryInput {
  connectionState: {
    isConnecting: boolean;
    sourceNodeId?: string | null;
    sourceSocketId?: string | null;
    tempEdge: { startX: number; startY: number; endX: number; endY: number } | null;
  };
  edges: Edge[];
  nodes: Node[];
  groups?: NodeGroup[];
  defaultRoutingMode?: 'bezier' | 'orthogonal';
  forceLayoutEdges?: boolean;
  /** Colour wires by where they land: the frame they enter, else the node they converge on. */
  tintEdgesByDestination?: boolean;
}

export function useConnectionGeometry({
  connectionState,
  edges,
  nodes,
  groups = [],
  defaultRoutingMode = 'bezier',
  forceLayoutEdges = false,
  tintEdgesByDestination = true,
}: ConnectionGeometryInput) {
  const tempEdgePoints = useMemo(() => {
    if (!connectionState.isConnecting || !connectionState.tempEdge) return null;
    const { startX, startY, endX, endY } = connectionState.tempEdge;
    const srcNode = connectionState.sourceNodeId
      ? nodes.find(n => n.id === connectionState.sourceNodeId)
      : undefined;
    const srcSock = srcNode && connectionState.sourceSocketId
      ? srcNode.sockets.find(s => s.id === connectionState.sourceSocketId)
      : undefined;
    const exit = srcSock && srcNode
      ? getSocketOutwardDir(srcSock, srcNode.size)
      : { dx: 1, dy: 0 };
    const dist = Math.hypot(endX - startX, endY - startY);
    const offset = bezierControlOffset(dist);
    return [
      startX, startY,
      startX + exit.dx * offset, startY + exit.dy * offset,
      endX, endY,
      endX, endY,
    ];
  }, [connectionState.isConnecting, connectionState.tempEdge, connectionState.sourceNodeId, connectionState.sourceSocketId, nodes]);

  const nodeById = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);

  const orthoMidShifts = useMemo(
    () => computeOrthoMidShifts(edges, nodes, defaultRoutingMode),
    [edges, nodes, defaultRoutingMode],
  );

  // Routed as a set, so corridors two edges would otherwise share get pulled apart.
  const orthoPaths = useMemo(
    () => computeOrthoPaths(edges, nodes, defaultRoutingMode, orthoMidShifts),
    [edges, nodes, defaultRoutingMode, orthoMidShifts],
  );

  const edgeTints = useMemo(
    () => (tintEdgesByDestination ? buildEdgeTints(edges, nodes, groups) : {}),
    [tintEdgesByDestination, edges, nodes, groups],
  );

  const jumperPoints = useMemo(() => [], []);

  const forceAdjustedCPs = useMemo<AdjustedControlPoints>(() => {
    if (!forceLayoutEdges) return {};
    const getSocket = (nodeId: string, socketId: string) =>
      nodeById.get(nodeId)?.sockets.find(s => s.id === socketId);
    return computeForceAdjustedControlPoints(edges, nodes, getSocket, 4, orthoMidShifts);
  }, [forceLayoutEdges, edges, nodes, nodeById, orthoMidShifts]);

  return { tempEdgePoints, jumperPoints, forceAdjustedCPs, orthoMidShifts, orthoPaths, edgeTints };
}
